/** App frame: sidebar navigation, live crawler status, page outlet. */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { NavLink, Outlet } from 'react-router-dom'

import { api } from '@/api/client'
import { cn, formatNumber } from '@/lib/utils'

const NAV = [
  { to: '/', label: 'Overview', icon: 'M4 13h6V4H4v9zm0 7h6v-5H4v5zm10 0h6v-9h-6v9zm0-16v5h6V4h-6z' },
  { to: '/companies', label: 'Companies', icon: 'M3 21h18M5 21V7l7-4 7 4v14M9 9h1m-1 4h1m4-4h1m-1 4h1M10 21v-4h4v4' },
  { to: '/jobs', label: 'Jobs', icon: 'M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2m-9 0h12a2 2 0 012 2v9a2 2 0 01-2 2H6a2 2 0 01-2-2V9a2 2 0 012-2z' },
  { to: '/scheduler', label: 'Scheduler', icon: 'M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z' },
]

export function Shell() {
  const queryClient = useQueryClient()
  const stats = useQuery({ queryKey: ['stats'], queryFn: api.stats, refetchInterval: 10_000 })
  const session = useQuery({ queryKey: ['session'], queryFn: api.session, staleTime: Infinity })
  const s = stats.data

  async function signOut() {
    await api.logout()
    queryClient.clear()
    await queryClient.invalidateQueries({ queryKey: ['session'] })
  }

  return (
    <div className="flex min-h-dvh">
      <aside className="sticky top-0 hidden h-dvh w-[232px] shrink-0 flex-col border-r border-line bg-surface px-3 py-5 md:flex">
        <div className="mb-7 flex items-center gap-2.5 px-2">
          <div className="grid h-8 w-8 place-items-center rounded-[9px] bg-ink text-[13px] font-semibold text-white">JS</div>
          <div className="leading-tight">
            <p className="text-[14.5px] font-semibold">Job Scheduler</p>
            <p className="text-[11.5px] text-ink-muted">Companies &amp; openings</p>
          </div>
        </div>
        <nav className="flex flex-col gap-0.5">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-field px-3 py-2 text-[14px] transition',
                  isActive ? 'bg-surface-muted font-medium text-ink' : 'text-ink-muted hover:bg-surface-muted/70 hover:text-ink',
                )
              }
            >
              <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
                <path d={item.icon} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto rounded-field border border-line bg-surface-muted px-3 py-3 text-[12.5px]">
          <div className="mb-1.5 flex items-center gap-2 font-medium text-ink">
            <span
              className={cn(
                'h-2 w-2 rounded-full',
                stats.isError ? 'bg-danger' : s?.crawling_now ? 'animate-pulse bg-success' : 'bg-ink-faint',
              )}
            />
            {stats.isError ? 'API unreachable' : s?.crawling_now ? `Checking ${s.crawling_now}` : 'Scheduler idle'}
          </div>
          <p className="text-ink-muted">
            {s ? `${formatNumber(s.companies_total)} companies · ${formatNumber(s.jobs_active)} open jobs` : '—'}
          </p>
        </div>
        {session.data?.login_required && (
          <button
            type="button"
            onClick={signOut}
            className="mt-2 rounded-field px-3 py-2 text-left text-[13px] text-ink-muted transition hover:bg-surface-muted hover:text-ink"
          >
            Sign out{session.data.username ? ` (${session.data.username})` : ''}
          </button>
        )}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile nav */}
        <nav className="flex gap-1 overflow-x-auto border-b border-line bg-surface px-3 py-2 md:hidden">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cn('whitespace-nowrap rounded-field px-3 py-1.5 text-[13.5px]', isActive ? 'bg-surface-muted font-medium' : 'text-ink-muted')
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <main className="mx-auto w-full max-w-[1320px] flex-1 px-4 py-7 sm:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
